import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle2,
  Clock3,
  FileQuestion,
  Loader2,
  RotateCcw,
  Send,
  Sparkles,
} from "lucide-react";
import Headerdash from "../head";
import Sidebar from "../sidebar";
import QuestionBox from "./questionbox";
import SpacingBox from "./Spacing";
import MCQQuestion from "./Mcq";
import useFetchData from "../fetchpostData";
import Notification from "./Notification";
import ScoreBoxForReading from "./ResponseBoxReadingSection";
import ScoreCardForReading from "./ScoreBoxForReadingSection";
import Timers from "../PageComponent/Timers";

const ReadingShell = ({ children }) => (
  <div className="min-h-screen bg-slate-50 text-gray-700">
    <Headerdash />
    <Sidebar />
    <main className="min-h-screen pt-20 sm:pl-72">
      <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {children}
      </div>
    </main>
  </div>
);

const StatusPill = ({ type, children }) => {
  const styles =
    type === "success"
      ? "bg-green-50 text-green-700 ring-green-100"
      : type === "warning"
      ? "bg-amber-50 text-amber-700 ring-amber-100"
      : "bg-purple-50 text-purple-700 ring-purple-100";

  return (
    <span
      className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-bold uppercase ring-1 ${styles}`}
    >
      {children}
    </span>
  );
};

export default function MultiTypeSingleAnswerPage() {
  const { title } = useParams();
  const section = "multipletypesingleanswer";
  const practiceItemId = title;
  const post = useFetchData(section, title);

  const [originalWords, setOriginalWords] = useState("");
  const [mcqQuestion, setMcqQuestion] = useState("");
  const [questionTitle, setQuestionTitle] = useState("");
  const [paraTitle, setParaTitle] = useState("");
  const [mcqOptions, setMcqOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isAttempted, setIsAttempted] = useState(false);
  const [message, setMessage] = useState("");
  const [showNotification, setShowNotification] = useState(false);
  const [notificationType, setNotificationType] = useState("");
  const [savedPractice, setSavedPractice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (post && post.question) {
      setOriginalWords(post.question);
      setMcqQuestion(post.question_mcq);
      setQuestionTitle(post.q_title || "Multiple-choice, choose single answer");
      setParaTitle(post.title);
      setMcqOptions(post.option || []);
    }
  }, [post]);

  const savePractice = async (practiceData) => {
    try {
      const response = await fetch("/api/save-multipletypesingleanswer", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(practiceData),
      });

      if (!response.ok) {
        throw new Error("Failed to save practice data");
      }

      return await response.json();
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setSubmitted(true);

    const practiceData = {
      practiceType: section,
      practiceItemId,
      response: {
        questionId: practiceItemId,
        answer: selectedOption,
        isAttempted,
      },
    };

    try {
      const result = await savePractice(practiceData);
      setMessage("Practice data saved successfully!");
      setNotificationType("success");
      setSavedPractice(result);
      setShowNotification(true);

      setTimeout(() => {
        setIsAttempted(true);
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error("Error saving practice data:", error);
      setMessage("Failed to save practice data.");
      setNotificationType("error");
      setShowNotification(true);
      setLoading(false);
      setSubmitted(false);
    }
  };

  const handleRetake = () => {
    setSelectedOption(null);
    setSubmitted(false);
    setIsAttempted(false);
    setSavedPractice(null);
  };

  return (
    <ReadingShell>
      <section className="mb-6 flex flex-col gap-5 border-b border-slate-200 pb-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase text-purple-700">
            Reading practice
          </p>
          <h1 className="mt-1 text-2xl font-extrabold text-gray-900 sm:text-3xl">
            {questionTitle || "Multiple-choice, choose single answer"}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-gray-500 sm:text-base">
            Read the passage, understand the question, and select the one best
            response.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <StatusPill>Medium</StatusPill>
          <StatusPill type={isAttempted ? "success" : "warning"}>
            {isAttempted ? "Attempted" : "Not attempted"}
          </StatusPill>
          <span className="inline-flex min-h-9 items-center gap-2 rounded-md bg-white px-3 py-2 text-sm font-bold text-gray-700 shadow-sm ring-1 ring-slate-200">
            <Clock3 className="h-4 w-4 text-purple-700" aria-hidden="true" />
            <Timers initialSeconds={0} />
          </span>
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr),360px]">
        <div className="space-y-6">
          <section className="rounded-md border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
                <FileQuestion className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold uppercase text-purple-700">
                  Instructions
                </p>
                <h2 className="text-xl font-extrabold text-gray-900">
                  Choose one answer
                </h2>
                <p className="mt-2 text-sm leading-6 text-gray-600">
                  Read the text and answer the question by selecting the
                  correct response. Only one response is correct.
                </p>
              </div>
            </div>
          </section>

          <QuestionBox question={originalWords} />

          <MCQQuestion
            question={mcqQuestion}
            options={mcqOptions}
            onOptionSelect={setSelectedOption}
            selectedOption={selectedOption}
            submitted={submitted}
          />

          <section className="flex flex-col gap-3 rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:p-5">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
                <Sparkles className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold uppercase text-gray-500">
                  Ready to score?
                </p>
                <p className="text-sm text-gray-500">
                  {selectedOption
                    ? "Submit your selected answer when you are confident."
                    : "Select one answer before submitting."}
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-violet-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
                onClick={handleSubmit}
                disabled={submitted || loading || !selectedOption}
              >
                {loading ? (
                  <>
                    <Loader2
                      className="h-4 w-4 animate-spin"
                      aria-hidden="true"
                    />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" aria-hidden="true" />
                    Submit
                  </>
                )}
              </button>

              {submitted && (
                <button
                  type="button"
                  onClick={handleRetake}
                  className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-slate-100 px-5 py-2.5 text-sm font-bold text-gray-700 transition hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-2"
                >
                  <RotateCcw className="h-4 w-4" aria-hidden="true" />
                  Retake
                </button>
              )}
            </div>
          </section>

          {submitted && (
            <section className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-5 flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-md bg-green-50 text-green-700">
                  <CheckCircle2 className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <p className="text-sm font-semibold uppercase text-purple-700">
                    Result
                  </p>
                  <h2 className="text-xl font-extrabold text-gray-900">
                    Latest score
                  </h2>
                </div>
              </div>
              <div className="flex justify-center py-6">
                <ScoreCardForReading
                  score={savedPractice?.savedResponse?.totalScore}
                  maxScore={1}
                />
              </div>
            </section>
          )}
        </div>

        <aside className="space-y-6">
          {paraTitle && (
            <section className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
              <p className="text-sm font-semibold uppercase text-gray-500">
                Passage title
              </p>
              <p className="mt-1 text-base font-extrabold text-gray-900">
                {paraTitle}
              </p>
            </section>
          )}

          <section className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-amber-50 text-amber-700">
                <AlertCircle className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold uppercase text-gray-500">
                  Practice tip
                </p>
                <p className="mt-1 text-sm leading-6 text-gray-600">
                  Find the answer in the passage first, then compare every
                  option against that evidence before choosing.
                </p>
              </div>
            </div>
          </section>

          <ScoreBoxForReading
            originalWords={originalWords}
            practiceItemId={practiceItemId}
            onIsAttempted={setIsAttempted}
          />
        </aside>
      </div>

      <SpacingBox />

      {showNotification && (
        <Notification
          message={message}
          type={notificationType}
          onClose={() => setShowNotification(false)}
        />
      )}
    </ReadingShell>
  );
}
